import { CalendarDays, ChevronDown } from "lucide-react";
import type { GroomingUpcomingSession } from "@ttc-spectree/toolbox-contract";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUpcomingSessions } from "@/hooks/queries/use-grooming";
import { shortMeetingDate } from "@/lib/grooming/meeting-dates";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * What a meeting's agenda holds, in plain words: "not started" without an
 * agenda, else the item count and, once briefs were prepared, that too.
 *
 * @example sessionState({ batchId: "b", status: "completed", itemCount: 4 }) === "4 items · briefs prepared"
 */
export function sessionState(session: Pick<GroomingUpcomingSession, "batchId" | "status" | "itemCount">): string {
  if (!session.batchId) return "not started";
  const count = session.itemCount === 1 ? "1 item" : `${String(session.itemCount)} items`;
  switch (session.status) {
    case "running":
      return `${count} · preparing briefs`;
    case "stopped":
    case "completed":
      return `${count} · briefs prepared`;
    default:
      return session.itemCount === 0 ? "no items yet" : count;
  }
}

/** One picker row: "Wed Nov 11 · 4 items · briefs prepared", "Wed Nov 25 · not started". */
export function sessionOptionLabel(session: GroomingUpcomingSession): string {
  return `${shortMeetingDate(session.date)} · ${sessionState(session)}`;
}

export interface SessionPickerProps {
  /** The ADO project whose upcoming meetings are listed. */
  project: string;
  /** The agenda the item is on now; its meeting is shown but cannot be picked. */
  currentBatchId?: string | null;
  /** Called with the picked meeting's date (`YYYY-MM-DD`). */
  onPick: (date: string) => void;
  /** Disables the trigger (a request in flight, or no permission). */
  disabled?: boolean;
  /** The trigger's words. */
  label?: string;
  /** Extra classes for the trigger. */
  className?: string;
  /** Test id of the trigger; each row is `${testId}-option-<date>`. */
  testId?: string;
}

/**
 * Pick one of the project's upcoming meetings (E323, ER-1710, feature
 * PI-SPEC-SW-1738): the shared "pick a meeting" menu the Session, Backlog and
 * reading pane screens use to add an item to a meeting or move it to another
 * one. Lists `useUpcomingSessions` earliest first, one row per meeting with
 * what its agenda holds; the meeting of `currentBatchId` (where the item is
 * now) is shown but disabled. Picking calls `onPick(date)`; the caller runs
 * the add or move and shows any error.
 */
export function SessionPicker({
  project,
  currentBatchId,
  onPick,
  disabled = false,
  label = "Pick a meeting",
  className,
  testId = "session-picker",
}: SessionPickerProps) {
  const upcoming = useUpcomingSessions(project);
  const sessions = upcoming.data?.sessions ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        type="button"
        disabled={disabled}
        data-testid={testId}
        className={cn(
          "inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-transparent px-2.5 font-medium transition-colors hover:bg-muted/50 disabled:pointer-events-none disabled:opacity-50",
          TOOLBOX_TEXT.meta,
          className
        )}
      >
        <CalendarDays className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        <span>{label}</span>
        <ChevronDown className="h-3.5 w-3.5 flex-none text-muted-foreground" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[260px]" aria-label="Upcoming meetings">
        {upcoming.isPending ? (
          <p className={cn("px-2 py-1.5 text-muted-foreground", TOOLBOX_TEXT.content)} data-testid={`${testId}-loading`}>
            Reading the upcoming meetings…
          </p>
        ) : upcoming.isError ? (
          <p className={cn("px-2 py-1.5 text-muted-foreground", TOOLBOX_TEXT.content)} data-testid={`${testId}-error`}>
            The upcoming meetings could not be read. Try again in a moment.
          </p>
        ) : sessions.length === 0 ? (
          <p className={cn("px-2 py-1.5 text-muted-foreground", TOOLBOX_TEXT.content)} data-testid={`${testId}-empty`}>
            No upcoming meetings yet. The meeting schedule is set on Session.
          </p>
        ) : (
          sessions.map((session) => {
            const isCurrent = Boolean(currentBatchId) && session.batchId === currentBatchId;
            return (
              <DropdownMenuItem
                key={session.date}
                data-testid={`${testId}-option-${session.date}`}
                aria-disabled={isCurrent || undefined}
                data-current={isCurrent ? "true" : undefined}
                className={cn("gap-2", isCurrent && "cursor-not-allowed opacity-60 hover:bg-transparent focus:bg-transparent")}
                onClick={() => {
                  if (!isCurrent) onPick(session.date);
                }}
              >
                <span className={cn("flex-1", TOOLBOX_TEXT.content)}>{sessionOptionLabel(session)}</span>
                {isCurrent ? <span className={cn("text-muted-foreground", TOOLBOX_TEXT.meta)}>here now</span> : null}
              </DropdownMenuItem>
            );
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
